import { hasFirstPartyFrame } from "@/lib/errors/no-first-party-frame-fingerprint";

/**
 * Detects TypeErrors thrown by scripts that a browser, in-app browser or
 * extension injects into our pages. Each one reads a property that exists
 * nowhere in our code, so the property name in the message identifies the
 * injector:
 *
 * - `WX_Trans_id`: WeChat's in-app browser (`MicroMessenger`) translate
 *   script. Its frames point at the page document, and the stack carries the
 *   script's own function names (`safeSetNodeHash`, `processExtraInfo`,
 *   `textNodesUnder`).
 * - `savePasskeys`: a Safari password-manager extension. Frameless.
 * - `tabId`: a WebExtension messaging field, read by an extension content
 *   script. Its stack is `[native code]` only.
 * - `Error: pa`: Chrome for iOS's built-in Google Translate. Chrome evaluates
 *   the translate element script into the page, so its frames carry no URL
 *   (`at Eh @ undefined`) apart from one pointing at the page document. It
 *   throws right after `translate_http` loads, with a minified one-word
 *   message.
 *
 * Keyed on those exact signatures, never on "frameless" alone, and only
 * when no frame points at our own `/_next/static/` bundle, so a real bug that
 * happens to read one of these names still reaches Error Tracking.
 */
const INJECTED_SCRIPT_PROPERTY_MESSAGE_PATTERNS = [
  // Safari / WebKit: "null is not an object (evaluating 'node.WX_Trans_id')"
  /^(?:Uncaught )?(?:TypeError: )?(?:null|undefined) is not an object \(evaluating '[A-Za-z_$][\w$]{0,3}\.(?:WX_Trans_id|savePasskeys|tabId)'\)$/,
  // Chromium (Android WeChat's XWeb): "Cannot read properties of null (reading 'WX_Trans_id')"
  /^(?:Uncaught )?(?:TypeError: )?Cannot (?:read|set) properties of (?:null|undefined) \((?:reading|setting) '(?:WX_Trans_id|savePasskeys|tabId)'\)$/,
];

/** Function names from WeChat's injected translate script. */
const WECHAT_TRANSLATE_FRAME_PATTERN =
  /^at (?:safeSetNodeHash|processExtraInfo|textNodesUnder) @ /;

/** The minified message Chrome for iOS's Google Translate script throws. */
const CHROME_IOS_TRANSLATE_MESSAGE_PATTERN = /^(?:Uncaught )?(?:Error: )?pa$/;

/**
 * A frame with no source URL, which the RUM SDK prints as `@ undefined`:
 * script the browser evaluated into the page rather than loaded from a URL.
 */
const URL_LESS_FRAME_PATTERN = /^at \S+ @ undefined$/;

export function isInjectedScriptPropertyNoise(
  message: string | undefined,
  stack: string | undefined,
): boolean {
  if (!message) {
    return false;
  }

  if (hasFirstPartyFrame(stack)) {
    return false;
  }

  if (
    INJECTED_SCRIPT_PROPERTY_MESSAGE_PATTERNS.some((pattern) =>
      pattern.test(message.trim()),
    )
  ) {
    return true;
  }

  const frames = (stack ?? "").split("\n").map((line) => line.trim());

  if (
    CHROME_IOS_TRANSLATE_MESSAGE_PATTERN.test(message.trim()) &&
    frames.some((frame) => URL_LESS_FRAME_PATTERN.test(frame))
  ) {
    return true;
  }

  return frames.some((frame) => WECHAT_TRANSLATE_FRAME_PATTERN.test(frame));
}
